import type { Ref } from 'vue';
import { computed, ref } from 'vue';
import { t } from '@getaccept/lib-shared/src/helpers/translation.helper';
import { useLaunchDarkly } from '@getaccept/lib-shared/src/launch-darkly/launch-darkly.composable';
import type { ProductProperty } from '@getaccept/lib-shared/src/product-library/product-properties/types/product-property';
import { nanoid } from 'nanoid';
import type {
  PricingTableColumn,
  PricingTableSection,
  PricingTableColumnType,
} from '../types/pricing-table';
import { PricingTableHelper } from '../helpers/pricing-table.helper';
import { EPricingTableColumn } from '../types/enums';
import { maxCustomColumns, minColumnWidth, nonGridColumns } from '../constants/pricing-table';
import { EditorHelper } from '../helpers/editor.helper';

export function usePricingTableColumns(
  section: Ref<PricingTableSection>,
  preCalculated: Ref<boolean>,
  productProperties: Ref<ProductProperty[]> = ref([])
) {
  const { flags } = useLaunchDarkly();

  const columns = computed(() => section.value?.columns || []);

  const enabledColumns = computed(() => PricingTableHelper.getEnabledColumns(columns.value));
  const columnWidths = computed(() => enabledColumns.value?.map(({ width }) => width));

  const customColumns = computed(() => columns.value.filter(({ isCustom }) => isCustom));
  const customColumnThresholdReached = computed(() => {
    const maxColumns = flags.value.pricingTableMultipleColumns ? maxCustomColumns : 1;
    return customColumns.value.length >= maxColumns;
  });

  const maxEnabledColumnsReached = computed(
    () => enabledColumns.value.length >= Math.floor(100 / minColumnWidth)
  );

  const getColumnById = (columnId: string) => columns.value.find(({ id }) => id === columnId);

  const getColumnByName = (columnName: EPricingTableColumn) =>
    columns.value.find(({ name }) => name === columnName);

  const isColumnEnabled = (column: PricingTableColumn) => column?.enabled || false;
  const isGridColumn = (column: PricingTableColumn) =>
    !nonGridColumns.includes(column.name as EPricingTableColumn);

  const enableColumn = (column: PricingTableColumn) =>
    preCalculated.value
      ? PricingTableHelper.enableColumn(column.id, section.value)
      : PricingTableHelper.enableColumnAndReCalculate(column.id, section.value);

  const disableColumn = (column: PricingTableColumn) => {
    let updatedSection = preCalculated.value
      ? PricingTableHelper.disableColumn(column.id, section.value)
      : PricingTableHelper.disableColumnAndReCalculate(column.id, section.value);

    if (column.name === EPricingTableColumn.TotalPrice) {
      const discountColumn = getColumnByName(EPricingTableColumn.Discount);
      updatedSection = preCalculated.value
        ? PricingTableHelper.disableColumn(discountColumn.id, updatedSection)
        : PricingTableHelper.disableColumnAndReCalculate(discountColumn.id, updatedSection);
    }

    return updatedSection;
  };

  const discountColumn = computed(() => getColumnByName(EPricingTableColumn.Discount));
  const totalPriceColumn = computed(() => getColumnByName(EPricingTableColumn.TotalPrice));

  const productPropertyIds = computed(() => (productProperties.value || []).map(({ id }) => id));

  const isColumnAProductPropertyReference = (column: PricingTableColumn) =>
    productPropertyIds.value.includes(column.productPropertyId);

  const getColumnByProductPropertyId = (productPropertyId: string) =>
    columns.value.find(column => column.productPropertyId === productPropertyId);

  const getProductPropertyById = (productPropertyId: string) =>
    (productProperties.value || []).find(({ id }) => id === productPropertyId);

  const isProductPropertyEnabled = (productPropertyId: string) =>
    isColumnEnabled(getColumnByProductPropertyId(productPropertyId));

  const enableProductProperty = (productPropertyId: string) => {
    const existingColumn = getColumnByProductPropertyId(productPropertyId);

    if (existingColumn) {
      return enableColumn(existingColumn);
    }

    const column: PricingTableColumn = {
      id: nanoid(EditorHelper.NANO_ID_LENGTH),
      productPropertyId,
      enabled: true,
      width: minColumnWidth,
    };

    const updatedSection = PricingTableHelper.insertColumn(column, section.value);

    return preCalculated.value
      ? PricingTableHelper.enableColumn(column.id, updatedSection)
      : PricingTableHelper.enableColumnAndReCalculate(column.id, updatedSection);
  };

  const disableProductProperty = (productPropertyId: string) =>
    disableColumn(getColumnByProductPropertyId(productPropertyId));

  const toggleProductProperty = (productPropertyId: string) => {
    const toggleFn = isProductPropertyEnabled(productPropertyId)
      ? disableProductProperty
      : enableProductProperty;

    return toggleFn(productPropertyId);
  };

  const getColumnDisplayName = (column: PricingTableColumn) => {
    if (column.displayName) {
      return column.displayName;
    }

    if (isColumnAProductPropertyReference(column)) {
      return getProductPropertyById(column.productPropertyId).displayName;
    }

    const translationKey =
      column.name === EPricingTableColumn.TotalPrice ? 'total-price' : column.name;

    return translationKey ? t(translationKey) : '';
  };

  const getColumnType = (column: PricingTableColumn): PricingTableColumnType => {
    if (isColumnAProductPropertyReference(column)) {
      return getProductPropertyById(column.productPropertyId).type;
    }

    if (column.columnType) {
      return column.columnType;
    }

    if (column.isCustom) {
      return 'Text';
    }

    switch (column.name) {
      case EPricingTableColumn.Name:
      case EPricingTableColumn.Description:
      case EPricingTableColumn.Sku:
        return 'Text';
      case EPricingTableColumn.Price:
      case EPricingTableColumn.Units:
      case EPricingTableColumn.Tax:
      case EPricingTableColumn.TotalPrice:
      case EPricingTableColumn.NetPrice:
      case EPricingTableColumn.Discount:
        return 'Number';
    }

    return 'Text';
  };

  return {
    columns,
    enabledColumns,
    columnWidths,
    customColumns,
    discountColumn,
    totalPriceColumn,
    customColumnThresholdReached,
    maxEnabledColumnsReached,
    getColumnDisplayName,
    getColumnByName,
    getColumnById,
    getColumnType,
    isColumnEnabled,
    isGridColumn,
    enableColumn,
    disableColumn,
    isColumnAProductPropertyReference,
    isProductPropertyEnabled,
    enableProductProperty,
    disableProductProperty,
    toggleProductProperty,
    getColumnByProductPropertyId,
  };
}
